import { useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import NodeChartCardHeader from './NodeChartCardHeader';
import NodeExamplesDialog from './NodeExamplesDialog';
import NodeStatTile from './NodeStatTile';
import NodeTimeseriesChart from './NodeTimeseriesChart';
import NodeHeatmapChart from './NodeHeatmapChart';
import NodeViewSwitch from './NodeViewSwitch';
import useNodeAggregate from './useNodeAggregate';
import { buildEntriesForChartSelection, buildHeatmapExamplesSelection } from './nodeFilterUtils';
import { buildRendererProps, resolveRenderer } from './nodeChartRegistry';
import { CHART_TYPES, isDegenerateDistribution, resolveChartSpec, resolveViews, VIEWS } from './nodeChartSpec';
import { applyValueShaping, CHART_HEIGHT, PLOT_AREA_CLASS } from './nodeChartTheme';
import { describeChartMeta, visibleDeclaredOptions } from './nodeHiddenValues';

/**
 * Оболочка карточки графика по узлу.
 *
 * Всё, что должно выглядеть одинаково независимо от типа графика, живёт здесь: шапка,
 * переключатель вида, состояния загрузки/ошибки/пустоты, подготовка строк, деградация
 * вырожденных карточек, переход в журнал и диалог примеров. Рендерер получает уже готовые
 * данные и рисует только сам график — поэтому он физически не может завести свою шапку
 * или свою высоту.
 */

/** Столбцам нужна высота по числу строк, остальным типам хватает фиксированной. */
const resolveHeight = (type, rowCount, size) => {
  if (type !== CHART_TYPES.Bar) {
    return CHART_HEIGHT[size] ?? CHART_HEIGHT.md;
  }

  return Math.min(460, Math.max(200, rowCount * 34 + 48));
};

const NodeChartCard = ({ chart, dateRange, callType = 'all', nodeFilter, packVersion, scope, onOpenCallJournal }) => {
  const [examples, setExamples] = useState(null);
  const { rows, denominator, skipped, hiddenCalls, packVersions, isLoading, error, reload } = useNodeAggregate({
    chart,
    dateRange,
    callType,
    nodeFilter,
    packVersion,
    scope
  });

  const spec = useMemo(() => resolveChartSpec(chart), [chart]);
  const views = useMemo(() => resolveViews(spec), [spec]);
  const [view, setView] = useState(views[0]?.code ?? VIEWS.Distribution);

  // Объявленные значения лежат в `options` у категорий и в `items` у чек-листов — за вычетом
  // скрытых тенантом: иначе `fill_declared` добирал бы их нулевыми строками.
  const declaredOptions = useMemo(() => visibleDeclaredOptions(chart), [chart]);

  const shapedRows = useMemo(
    () => applyValueShaping(rows, spec, declaredOptions),
    [rows, spec, declaredOptions]
  );

  const isChecklist = spec.type === CHART_TYPES.ChecklistTable;

  // Список цитат тянет данные из своего эндпоинта, а не из агрегата: у текстовых выходов
  // агрегат вырождается в единственную строку «—», и общая логика приняла бы её за
  // вырожденное распределение и подменила карточку стат-плиткой.
  const ownsData = Boolean(resolveRenderer(spec.type).ownsData);

  // Решение о вырожденности принимается по данным, а не по каталогу: пакет объявляет
  // четыре класса, а за период встречается один.
  const isDegenerate =
    !isChecklist &&
    !ownsData &&
    spec.type !== CHART_TYPES.Stat &&
    isDegenerateDistribution(shapedRows);

  const renderer = useMemo(
    () => (isDegenerate ? { Component: NodeStatTile, plotArea: false } : resolveRenderer(spec.type)),
    [isDegenerate, spec.type]
  );
  const { Component, plotArea } = renderer;

  // Переход в журнал описывает срез целиком: кликнутое значение плюс условия панели фильтров.
  // Без них журнал шире карточки, и цифра на графике не сходится с числом строк. Направление
  // не передаётся: оно в верхней панели, общей с журналом, и уже совпадает.
  const handleSelect = (entry) => {
    if (!chart.supportsDrilldown || !onOpenCallJournal) {
      return;
    }

    const entries = buildEntriesForChartSelection(chart, entry?.code);
    if (entries.length === 0) {
      return;
    }

    const panelEntries = nodeFilter ? nodeFilter.split(';').filter(Boolean) : [];
    const merged = [...panelEntries, ...entries.filter((item) => !panelEntries.includes(item))];

    // До MAX_NODE_FILTERS здесь намеренно не режем: молча отброшенное условие вернуло бы
    // ровно то расхождение карточки и журнала, ради которого всё это и делается.
    onOpenCallJournal({
      node_filter: merged.length > 0 ? merged.join(';') : undefined
    });
  };

  // У чек-листа обязательно показываем и число пропущенных: чек-лист применяется только
  // к своему типу звонков (input_filter ноды), поэтому «оценено 1 из 25» — норма, а не
  // потеря данных. Без этой цифры карточки выглядят необъяснимо разными: у одной одна
  // строка оператора, у другой четыре, и непонятно, почему.
  const meta = describeChartMeta({ isChecklist, denominator, skipped, hiddenCalls, packVersions });

  // Не `shapedRows.length > 0`: при `sort: declared` объявленные значения добираются
  // нулями, поэтому у воронки без данных строки есть, а показывать нечего — карточка
  // рисовала воронку из нулевых этапов рядом с надписью «нет данных».
  const hasData = shapedRows.some((row) => row.calls > 0);
  const height = resolveHeight(isDegenerate ? CHART_TYPES.Stat : spec.type, shapedRows.length, spec.size);

  const dataProps = { chart, dateRange, callType, nodeFilter, packVersion, scope, height };

  const renderBody = () => {
    if (view === VIEWS.Dynamics) {
      return <NodeTimeseriesChart {...dataProps} />;
    }

    if (view === VIEWS.Heatmap) {
      return (
        <NodeHeatmapChart
          {...dataProps}
          onSelect={(selection) => setExamples(buildHeatmapExamplesSelection(selection))}
        />
      );
    }

    const rendererProps = buildRendererProps(renderer, {
      viewProps: {
        chart,
        spec,
        rows: shapedRows,
        denominator,
        height,
        onSelect: chart.supportsDrilldown ? handleSelect : undefined,
        onShowExamples: setExamples
      },
      queryProps: dataProps
    });
    const body = <Component {...rendererProps} />;

    return plotArea ? (
      <div className={PLOT_AREA_CLASS} style={{ minHeight: height }}>
        {body}
      </div>
    ) : (
      body
    );
  };

  // Пустое состояние касается только основного вида и только рендереров, живущих на
  // агрегате: у динамики, тепловой карты и цитат собственные данные и собственная
  // проверка на пустоту.
  const isPrimaryView = view === VIEWS.Distribution || view === VIEWS.Table;
  const showEmptyState = isPrimaryView && !ownsData && !hasData && !isLoading && !error;

  return (
    // h-full + flex-col: карточка занимает всю высоту ряда сетки, поэтому соседние карточки
    // с разной длиной заголовка выравниваются по нижней границе.
    <Card data-chart-id={chart.id} className="flex h-full flex-col">
      <NodeChartCardHeader
        title={chart.title}
        description={chart.description}
        meta={meta}
        isLoading={isLoading}
        onReload={reload}
        onShowExamples={chart.supportsDrilldown ? () => setExamples({ title: 'все значения' }) : undefined}
      />
      <CardContent className="flex flex-1 flex-col gap-2">
        <NodeViewSwitch views={views} value={view} onChange={setView} />

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
        )}

        {showEmptyState && <p className="text-sm text-muted-foreground">Нет данных за выбранный период.</p>}

        {!showEmptyState && !error && renderBody()}
      </CardContent>

      {examples && (
        <NodeExamplesDialog
          chart={chart}
          selection={examples}
          dateRange={dateRange}
          callType={callType}
          nodeFilter={nodeFilter}
          packVersion={packVersion}
          scope={scope}
          onClose={() => setExamples(null)}
        />
      )}
    </Card>
  );
};

export default NodeChartCard;
